import React, { useEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { ScrollableX } from "../../styles/shared/card";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { blurAnimation } from "../../styles/shared/loading";
import LoadingSpinner from "./loading-spinner";
import { TextContainer } from "../../styles/shared/table";
import TextInput from "./text-input";
import { GoogleIcon, ICON_ID } from "../icons/google-icon";
import { ApplicationSort } from "../../models/shared/application-sort";

export interface FetchParams {
  searchText?: string;
  sortColumn?: string;
  sortOrder?: ApplicationSort;
  reset?: boolean; // optional, if you want to reset paging
}


export type FetchMoreData = (params: FetchParams) => Promise<void>;

export interface ColumnConfig {
  label?: string;
  hidden?: boolean;
  name?: string;
  sort?: boolean
  render?: (value: any, row: { [key: string]: any }, parentRow?: any) => React.ReactNode;
  order?: number;
  align?: string | "center" | "left" | "right";
  colspan?: number;
  hideOnMobile?: boolean
}

export interface TableRowData {
  id: string | number;
  [key: string]: any;
  children?: TableRowData[];
  className?: string
}

interface TableViewProps {
  id: string;
  columnConfig?: { [key: string]: ColumnConfig };
  actionButton?: (row: { [key: string]: any }) => React.ReactNode;
  toolbar?: () => React.ReactNode;
  tableHeader?: () => React.ReactNode;
  filterBar?: (sort: ApplicationSort, sortColumn: string | undefined) => React.ReactNode;
  fetchData?: FetchMoreData
  data: TableRowData[];
  totalCount: number;
  preLoadedData?: boolean;
  emptyText: string;
  onRowClick?: (row: { [key: string]: any }) => void;
  showHeaders?: boolean;
  search?: boolean;
  cardRows?: boolean;
  groupChildrenInCardRows?: boolean;
}

export default function TableView({
  id,
  columnConfig = {},
  actionButton,
  toolbar,
  filterBar,
  tableHeader,
  fetchData,
  data,
  totalCount,
  emptyText,
  preLoadedData = false,
  showHeaders = false,
  search = false,
  cardRows = false,
  groupChildrenInCardRows = false,
  onRowClick,
}: TableViewProps) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const loadingRowRef = useRef<HTMLDivElement>(null);
  const [displayData, setDisplayData] = useState(data);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<ApplicationSort>(ApplicationSort.Asc);
  const [searchText, setSearchText] = useState<string>("");
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const activeRequestsRef = useRef(0);

  const beginLoading = () => {
    activeRequestsRef.current += 1;
    setLoading(true);
  };

  const endLoading = () => {
    activeRequestsRef.current = Math.max(0, activeRequestsRef.current - 1);
    if (activeRequestsRef.current === 0) {
      setLoading(false);
    }
  };

  const runFetch = (params: FetchParams) => {
    if (!fetchData) {
      return Promise.resolve();
    }

    beginLoading();
    return fetchData(params).finally(() => endLoading());
  };


  const syncHeaderScroll = () => {
    if (headerScrollRef.current && bodyScrollRef.current) {
      headerScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;
    }
  };

  useInfiniteScroll({
    dataLength: displayData.length,
    totalCount: totalCount,
    fetchData: runFetch,
    loading,
    loadingRowRef
  });


  useEffect(() => {
    setDisplayData(data);
  }, [data]);

  useEffect(() => {
    if (data.length === 0 && !preLoadedData && fetchData && !searchText && !loading) {
      setPageLoading(true);
      fetchData?.({
        searchText: "",
        sortColumn: sortColumn,
        sortOrder: sortOrder,
        reset: false
      }).finally(() => setPageLoading(false));
    }
  }, []);

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const searchData = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    setSearchText?.(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }

  const performSearch = (value: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setSearchText(value);
    runFetch({
      searchText: value,
      sortColumn,
      sortOrder,
      reset: true
    });
  }

  const handleSearchKeyDown = (event: React.KeyboardEvent<any>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const value = (event.target as HTMLInputElement)?.value || searchText || "";
      performSearch(value);
    }
  }

  if (pageLoading) {
    return (
      <ScrollableX>
        <NoContent>
          <LoadingSpinner></LoadingSpinner>
        </NoContent>
      </ScrollableX>
    );
  }

  const getFilteredColumns = (
    columns: string[],
    configPredicate: (column: string) => boolean
  ): string[] => {
    return columns.filter(configPredicate);
  };

  const calculateColspans = (
    columns: string[],
    columnConfig: { [key: string]: ColumnConfig }
  ): { sumOfColspans: number; numberOfColspanColumns: number } => {
    return columns.reduce(
      (acc, column) => {
        const colspan = columnConfig[column]?.colspan || 1;
        if (colspan > 1) {
          acc.sumOfColspans += colspan;
          acc.numberOfColspanColumns++;
        }
        return acc;
      },
      { sumOfColspans: 0, numberOfColspanColumns: 0 }
    );
  };

  const calculateColumnLength = (
    columns: string[],
    columnConfig: { [key: string]: ColumnConfig },
    actionButton: boolean
  ): number => {
    const { sumOfColspans, numberOfColspanColumns } = calculateColspans(columns, columnConfig);
    return columns.length + sumOfColspans - numberOfColspanColumns + (actionButton ? 1 : 0);
  };

  const deriveColumns = (
    data: any[],
    columnConfig: { [key: string]: ColumnConfig }
  ): string[] => {
    return data.length > 0
      ? Object.keys(data[0] ?? []).filter((column) => !columnConfig[column]?.hidden)
      : [];
  };

  const columns: string[] = deriveColumns(data, columnConfig);

  const sortedColumns: string[] = columns.sort((a, b) => {
    const orderA = columnConfig[a]?.order ?? Number.MAX_SAFE_INTEGER;
    const orderB = columnConfig[b]?.order ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });

  const columnLength: number = calculateColumnLength(sortedColumns, columnConfig, actionButton != null);

  const mobileColumns: string[] = getFilteredColumns(
    sortedColumns,
    (column) => columnConfig[column]?.hideOnMobile !== true
  );

  const mobileColumnLength: number = calculateColumnLength(mobileColumns, columnConfig, actionButton != null);

  return (
    <>
      {tableHeader && (
        tableHeader()
      )}
      {filterBar && (
        <FilterBar>
          {filterBar(sortOrder, sortColumn)}
        </FilterBar>
      )}

      {(search || toolbar) && (
        <Toolbar>
          {search && (
            <SearchWrapper>
              <TextInput id="txtSearch" autoBlurAfterChange label="Search" value={searchText ?? ""} onChange={(event) => searchData(event)} onKeyDown={handleSearchKeyDown} type="text" iconID={ICON_ID.Search} ></TextInput>
            </SearchWrapper>
          )}
          {loading && (
            <LoadingSpinner></LoadingSpinner>
          )}
          {toolbar && (
            toolbar()
          )}


        </Toolbar>
      )}

      <TableWrapper id={id} $toolbar={search} $cardRows={cardRows}>
        {showHeaders && (
          <TableHeader>
            <ScrollableX ref={headerScrollRef}>
              <HeaderRow $columnCount={columnLength} $mobileColumnCount={mobileColumnLength}>

                {sortedColumns.map((header) => {
                  const config = columnConfig[header];
                  return (
                    <TableCell
                      $header={true}
                      $colSpan={config?.colspan}
                      $hideOnMobile={config?.hideOnMobile ?? false}
                      key={`header_${header}`}
                      $align={config?.align ?? "left"}
                      onClick={() => {
                        if (columnConfig[header]?.sort) {
                          const newOrder =
                            sortColumn === header && sortOrder === ApplicationSort.Asc
                              ? ApplicationSort.Desc
                              : ApplicationSort.Asc;
                          setSortColumn(header);
                          setSortOrder(newOrder);

                          runFetch({
                            searchText,
                            sortColumn: header,
                            sortOrder: newOrder,
                            reset: true
                          });
                        }
                      }}
                    >
                      {config?.name ?? header}
                      {sortColumn === header && (
                        <span>{sortOrder === ApplicationSort.Asc ? <GoogleIcon id={ICON_ID.ArrowUp} isContrast /> : <GoogleIcon id={ICON_ID.ArrowDown} isContrast />}</span>
                      )}
                    </TableCell>
                  );
                })}

              </HeaderRow>
            </ScrollableX>
          </TableHeader>

        )}
        <ScrollableX ref={bodyScrollRef} onScroll={syncHeaderScroll}>
          {searchLoading ? (
            <TableBody $cardRows={cardRows}>
              <LoadingRow>
                {sortedColumns.map((_column, index) => (
                  <LoadingCell key={`load${index}`}></LoadingCell>
                ))}
                <LoadingCell></LoadingCell>
              </LoadingRow>
            </TableBody>
          ) : (
            (displayData.length === 0 && !searchLoading && !loading) ? (
              <NoContent>
                <TextContainer>{emptyText}</TextContainer>
              </NoContent>
            ) : (
              <TableBody $cardRows={cardRows}>
                {displayData.map((row) => (
                  <div key={`dataRow-${row.id}`}>
                    <TableRow
                      className={row.className}
                      $mobileColumnCount={mobileColumnLength}
                      $columnCount={columnLength}
                      $cardRows={cardRows}
                      onClick={() => (onRowClick ? onRowClick(row) : null)}
                      ref={rowRef}

                    >
                      {sortedColumns.map((column) => (
                        <TableCell
                          $hideOnMobile={columnConfig[column]?.hideOnMobile ?? false}
                          $colSpan={columnConfig[column]?.colspan}
                          $align={columnConfig[column]?.align ?? "left"}
                          key={column}
                        >
                          {columnConfig[column]?.render
                            ? columnConfig[column].render!(row[column], row)
                            : row[column]}
                        </TableCell>
                      ))}
                      {actionButton && (
                        <TableCell $hideOnMobile={false} $align="center">{actionButton(row)}</TableCell>
                      )}
                    </TableRow>
                    {(!cardRows || !groupChildrenInCardRows) && (row.children ?? []).map((childRow: any) => (
                      <TableRow
                        className="childrow"
                        $mobileColumnCount={mobileColumnLength}
                        $columnCount={columnLength}
                        $cardRows={cardRows}
                        key={`childRow-${childRow.id || crypto.randomUUID()}`}
                      >
                        {sortedColumns.map((column) => (
                          <TableCell
                            $hideOnMobile={columnConfig[column]?.hideOnMobile ?? false}
                            $colSpan={columnConfig[column]?.colspan}
                            $align={columnConfig[column]?.align ?? "left"}
                            key={column}
                          >
                            <span style={{ paddingLeft: "20px", display: "block" }}>
                              {columnConfig[column]?.render
                                ? columnConfig[column].render!(childRow[column], childRow, row)
                                : childRow[column]}
                            </span>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </div>
                ))}


                {totalCount != displayData.length && (
                  <LoadingRow ref={loadingRowRef}>
                    {sortedColumns.map((_column, index) => (
                      <LoadingCell key={`loading${index}`}></LoadingCell>
                    ))}
                    <LoadingCell></LoadingCell>
                  </LoadingRow>
                )}
              </TableBody>
            ))}
        </ScrollableX>
      </TableWrapper >
    </>
  );
}

interface TableWrapperProps {
  $toolbar: boolean;
  $cardRows: boolean;
}


const TableWrapper = styled.div<TableWrapperProps>`
  display: grid;
  box-shadow: ${(props) => (props.$cardRows ? "none" : props.theme.application.boxShadow)};
  background: ${(props) => (props.$cardRows ? "transparent" : props.theme.application.background)};
`;

const TableBody = styled.div<{ $cardRows: boolean }>`
  display: grid;
  gap: ${(props) => (props.$cardRows ? "10px" : "0")};
  padding: ${(props) => (props.$cardRows ? "6px" : "0")};

`;

const TableHeader = styled.div`
  display: grid;
  position: sticky;
    background: ${(props) => props.theme.application.scheme.primary};
  top: 0;
  z-index: 2;
  color: ${(props) => props.theme.application.scheme.contrast};
`;

const SearchWrapper = styled.div`  
min-width: 30%;

`

const Toolbar = styled.div`  
display:flex;
width:100%;
flex-direction:row;
background:rgba(255, 255, 255, 0.5);
padding-left:5px; 
align-items: center;
gap:10px;
`
interface TableRowProps {
  $columnCount: number;
  $mobileColumnCount: number;
  $cardRows?: boolean;
}

const HeaderRow = styled.div<TableRowProps>`
  display: grid;    
  grid-template-columns: repeat(
    ${(props) => props.$columnCount},
    minmax(100px, 1fr)
  );
  cursor: pointer;
  align-items: center;
  font-size:0.8rem;

 @media (max-width: 600px) {
    grid-template-columns: repeat(${(props) => props.$mobileColumnCount}, minmax(100px, 1fr));
  }
  

`;

const TableRow = styled.div<TableRowProps>`
  display: grid;
  grid-template-columns: repeat(
    ${(props) => props.$columnCount},
    minmax(100px, 1fr)
  );
  border: ${(props) => (props.$cardRows ? `1px solid ${props.theme.application.border}` : `1px solid transparent`)};
  border-bottom: ${(props) => (props.$cardRows ? `1px solid ${props.theme.application.border}` : `1px solid ${props.theme.application.border}`)};
  border-radius: ${(props) => (props.$cardRows ? "7px" : "0")};
  cursor: pointer;
  align-items: center;
  background: ${(props) => (props.$cardRows
    ? "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,251,252,0.98) 100%)"
    : props.theme.application.table.cellBackground)};
  box-shadow: ${(props) => (props.$cardRows ? "0 2px 10px rgba(0, 0, 0, 0.06)" : "none")};
  &:hover {
    background: ${(props) => (props.$cardRows
    ? "linear-gradient(180deg, rgba(253,255,255,1) 0%, rgba(246,250,252,1) 100%)"
    : props.theme.application.table.hover)};
  }

  &.highlight {
    background: ${(props) => props.theme.application.table.highlight};
  }
 @media (max-width: 600px) {
    grid-template-columns: repeat(${(props) => props.$mobileColumnCount}, minmax(84px, 1fr));
  }


  &.childrow:not(:has(+ .childrow)) {
    border-bottom: ${(props) => (props.$cardRows ? `1px solid ${props.theme.application.border}` : `2px solid ${props.theme.application.border}`)};
  }

  &.childrow {
    background: ${(props) => (props.$cardRows
    ? "linear-gradient(180deg, rgba(248,251,252,0.98) 0%, rgba(242,246,248,0.98) 100%)"
    : "#f9f9f9")};
  }
`;

interface TableCellProps {
  $align: string;
  $colSpan?: number;
  $header?: boolean;
  $hideOnMobile: boolean;
}
const TableCell = styled.div<TableCellProps>`
  padding: 10px 8px;
  font-size:0.8rem;
  text-align: ${(props) => (props.$align ?? "center")};
  grid-column: ${(props) =>
    props.$colSpan ? `span ${props.$colSpan}` : "auto"};
  text-transform: ${(props) => (props.$header ? "capitalize" : "")};
  font-weight: ${(props) => (props.$header ? "600" : "")};

    @media (max-width: 600px) {
      display: ${(props) => (props.$hideOnMobile ? "none" : "block")}; 
  }

  background: transparent;
`;

const LoadingRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 10px;
  padding: 10px 0;
  animation: ${blurAnimation} 0.5s infinite;
  height: 50px;  
`;

const LoadingCell = styled.div`
  padding: 20px;
  border-radius: 15px;
  padding: 20px;
  border-radius: 6px;
  background: ${(props) => props.theme.application.placeholder};
  margin: 0px 16px;
`;

const NoContent = styled.div`
  text-align: center;
  margin-top: 10%;
  margin-bottom: 10%;
`;

const FilterBar = styled.div`
    display: flex;
    align-items: center;
    background-color: white;
    margin-bottom: 3px;
    flex-wrap: wrap;    
        justify-content: space-between;
        padding:0 5px 0px 5px;
    .MuiFormControl-root{
        min-width:200px;
    }
`;
